import * as perf from '../commons/performance-logger';
import { delay } from '../utils';
import { getLogger } from '../commons/logger';
import { releasePlayer } from './workerUtils';
import { reporter } from '../reports/reporter';
import { WorkerExtension } from './WorkerExtension';
import type { TestRunHandler } from '../testRunHandler';
import type { ExtensionPlayer } from './WorkerExtension';
import type { SerializedGrid } from 'src/services/gridService';

const logger = getLogger('base-worker');
const DELAY_BETWEEN_TESTS = 500;

export class WorkerExtensionSingleBrowser extends WorkerExtension {
    private testPlayer: ExtensionPlayer | null | undefined;

    async _releasePlayer() {
        if (!this.testPlayer) {
            return;
        }
        const { projectId } = this.userData || {};
        await releasePlayer(this.id, this.releaseSlotOnTestFinished, projectId!, this.options.company!.companyId, this.options.slotService, this.testPlayer);
        this.testPlayer = null;
    }

    override onQueueCompleted() {
        return this._releasePlayer();
    }

    override async getBrowserOnce(testRunHandler: TestRunHandler, customExtensionLocalLocation: string | undefined, player: ExtensionPlayer, gridInfo: SerializedGrid) {
        reporter.onGetSession(this.id, `worker ${this.id}`, testRunHandler.runMode);
        return this._getBrowserOnce(testRunHandler, customExtensionLocalLocation, player, gridInfo);
    }

    override async getTestPlayer(testRunHandler: TestRunHandler, customExtensionLocalLocation: string | undefined) {
        if (this.testPlayer && !this.testPlayer.driver.isAlive()) {
            logger.warn('WorkerExtensionSingleBrowser is releasing a dead player', { workerId: this.id });
            await this._releasePlayer();
        }
        if (!this.testPlayer) {
            this.testPlayer = await super.getTestPlayer(testRunHandler, customExtensionLocalLocation);
        }
        return this.testPlayer;
    }

    override async runTest(testRunHandler: TestRunHandler, customExtensionLocalLocation: string | undefined, shouldRerun: boolean | undefined) {
        const quarantineResult = this.handleQuarantine(testRunHandler);
        if (quarantineResult) {
            return quarantineResult;
        }

        perf.log('before runTest onTestStarted single browser');
        const test = await this.onTestStarted(this.id, testRunHandler.testId, testRunHandler.testResultId, shouldRerun, testRunHandler.retryKey);
        testRunHandler.baseUrl = test.config.baseUrl;
        const testPlayer = await this.getTestPlayer(testRunHandler, customExtensionLocalLocation);

        testRunHandler.markClearBrowser();
        return await this.runTestOnce(testRunHandler, testPlayer);
    }

    override async runTestCleanup() {
        if (!this.executionQueue.hasMoreTests()) {
            await this.onQueueCompleted();
            return;
        }
        if (this.options.lightweightMode?.general) {
            await delay(DELAY_BETWEEN_TESTS);
        }
    }
}
